import { useCallback, useEffect, useRef, useState } from 'react';

/** Matches `.v2-slide-out-right` in ui/v2/tokens.css. */
export const LEAVE_MS = 200;

const reducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const settle = () =>
  new Promise<void>((resolve) =>
    setTimeout(resolve, reducedMotion() ? 0 : LEAVE_MS),
  );

/**
 * A detail panel being put away — dismissed, closed, archived — leaves the
 * list it was opened from, so the panel goes with it. `leave(action)` runs
 * the action while the panel slides out (`leaving` → `v2-slide-out-right`),
 * then calls `onLeft` so the caller clears the selection. If the action
 * fails the panel comes back and the error is rethrown for the caller to
 * show. Nothing happens if the panel unmounted meanwhile, e.g. because
 * another row was opened.
 */
export function useLeave(onLeft: () => void) {
  const [leaving, setLeaving] = useState(false);
  const latest = useRef(onLeft);
  latest.current = onLeft;
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const leave = useCallback(async (action: () => Promise<unknown>) => {
    setLeaving(true);
    const [outcome] = await Promise.allSettled([action(), settle()]);
    if (!mounted.current) return;
    if (outcome.status === 'rejected') {
      setLeaving(false);
      throw outcome.reason;
    }
    latest.current();
  }, []);

  return { leaving, leave };
}

/**
 * `value`, frozen at its last value while `hold` is true. A panel on its
 * way out keeps showing what the user acted on rather than flashing the
 * updated record (Dismiss → Reopen, Archive → Restore) mid-slide.
 */
export function useHeld<T>(value: T, hold: boolean): T {
  const held = useRef(value);
  if (!hold) held.current = value;
  return held.current;
}
